/**
 * Parameter audit: the PARM messages in a log against a golden set per airframe.
 *
 * Scope is deliberately narrow — the failsafe and arming gates that decide what
 * the aircraft does when something goes wrong. A full parameter diff would be
 * thousands of rows of noise; this is the subset where a wrong value is a
 * safety event rather than a tuning preference.
 *
 * Names drift between firmware versions and vehicle types (Copter's
 * FS_GCS_ENABLE is Plane's FS_GCS_ENABL). A parameter reported missing may mean
 * "not on this firmware", not "not configured" — which is why missing entries
 * are reported separately from deviations, and never as critical.
 *
 * ponytail: GOLDEN lives in code. Move it to a per-org backend record the day
 * two airframes need different sets without a deploy.
 */

const EPS = 1e-6;

/** Declarative expectations, so the golden set stays editable without touching logic. */
const describe = (expect) => {
  if (expect.equals != null) return `= ${expect.equals}`;
  if (expect.not != null) return `≠ ${expect.not}`;
  if (expect.min != null && expect.max != null) return `between ${expect.min} and ${expect.max}`;
  if (expect.min != null) return `≥ ${expect.min}`;
  if (expect.max != null) return `≤ ${expect.max}`;
  return "set";
};

const passes = (value, expect) => {
  if (expect.equals != null) return Math.abs(value - expect.equals) < EPS;
  if (expect.not != null) return Math.abs(value - expect.not) >= EPS;
  if (expect.min != null && value < expect.min) return false;
  if (expect.max != null && value > expect.max) return false;
  return true;
};

// Shared across every ArduPilot vehicle.
const COMMON = [
  {
    name: "ARMING_CHECK",
    expect: { equals: 1 },
    severity: "critical",
    why: "Anything but 'all checks' lets the aircraft arm with a fault the firmware already knows about.",
  },
];

const COPTER = [
  ...COMMON,
  {
    name: "FS_THR_ENABLE",
    expect: { not: 0 },
    severity: "critical",
    why: "With RC failsafe disabled, losing the transmitter leaves the aircraft flying its last stick input.",
  },
  {
    name: "BATT_LOW_VOLT",
    expect: { min: 0.1 },
    severity: "critical",
    why: "No low-voltage threshold means no low-battery failsafe, whatever the action is set to.",
  },
  {
    name: "BATT_FS_LOW_ACT",
    expect: { not: 0 },
    severity: "critical",
    why: "A low-battery threshold with no action only writes a log line while the pack keeps draining.",
  },
  {
    name: "BATT_FS_CRT_ACT",
    expect: { not: 0 },
    severity: "critical",
    why: "The critical-battery tier is the last automatic chance to put the aircraft down under control.",
  },
  {
    name: "FS_EKF_ACTION",
    expect: { not: 0 },
    severity: "critical",
    why: "Without an EKF failsafe the aircraft keeps flying a position estimate it has already rejected.",
  },
  {
    name: "FS_EKF_THRESH",
    expect: { max: 0.8 },
    severity: "warn",
    why: "A permissive threshold delays the EKF failsafe until the estimate is badly wrong.",
  },
  {
    name: "FS_CRASH_CHECK",
    expect: { equals: 1 },
    severity: "warn",
    why: "Crash check disarms on impact; disabled, motors keep driving into whatever the aircraft hit.",
  },
  {
    name: "FS_GCS_ENABLE",
    expect: { not: 0 },
    severity: "warn",
    why: "Only matters if the flight depends on a GCS link — but if it does, this is the failsafe for losing it.",
  },
  {
    name: "RTL_ALT",
    expect: { min: 1500 },
    severity: "warn",
    why: "RTL below ~15 m (1500 cm) returns the aircraft through obstacles it climbed over on the way out.",
  },
];

const PLANE = [
  ...COMMON,
  {
    name: "THR_FAILSAFE",
    expect: { equals: 1 },
    severity: "critical",
    why: "Plane's RC failsafe gate. Disabled, throttle-channel loss is not detected at all.",
  },
  {
    name: "FS_LONG_ACTN",
    expect: { not: 0 },
    severity: "critical",
    why: "The long-failsafe action is what recovers the aircraft after a sustained link loss.",
  },
  {
    name: "FS_SHORT_ACTN",
    expect: { not: 0 },
    severity: "warn",
    why: "Short failsafe catches brief dropouts before they become a long failsafe.",
  },
];

/**
 * Golden sets keyed by the same loose airframe match the findings rules use.
 * A tail-sitter is a quadplane: it carries Plane's failsafes plus the VTOL
 * assist gates that keep it flyable when the wing stops working.
 */
export const GOLDEN = {
  copter: COPTER,
  plane: PLANE,
  tailsitter: [
    ...PLANE,
    {
      name: "Q_ENABLE",
      expect: { equals: 1 },
      severity: "critical",
      why: "The VTOL side of the airframe is inert without it.",
    },
    {
      name: "Q_ASSIST_SPEED",
      expect: { min: 0.1 },
      severity: "critical",
      why: "Zero disables VTOL assist, so a stall or slow transition has no motor authority to recover with.",
    },
  ],
};

export const goldenFor = (log) => {
  const v = (log?.vehicle_type || "").toLowerCase();
  const key = Object.keys(GOLDEN).find((k) => v.includes(k));
  return key ? { key, rules: GOLDEN[key] } : { key: null, rules: COMMON };
};

/** Does this log even carry parameters? */
export const hasParams = (log) => Boolean(log?.message_index?.PARM);

/**
 * Pull PARM into a flat name → value map. Parameters can be written more than
 * once in a flight; the last write is what the aircraft was actually using.
 */
export const fetchParams = async (logId, fetchMessages, { maxPoints = 20000 } = {}) => {
  const res = await fetchMessages(logId, { type: "PARM", fields: ["Name", "Value"], maxPoints });
  const values = {};
  for (const p of res?.points || []) {
    if (p?.Name != null && p?.Value != null) values[p.Name] = Number(p.Value);
  }
  return { values, truncated: Boolean(res?.truncated), count: Object.keys(values).length };
};

/**
 * Compare a parameter map against the golden set for this airframe.
 *
 * `truncated` matters: a downsampled PARM series cannot prove a parameter is
 * absent, so missing entries are suppressed rather than reported as findings
 * built on a gap in the data.
 */
export const auditParams = (log, { values = {}, truncated = false } = {}) => {
  const { key, rules } = goldenFor(log);
  const deviations = [];
  const missing = [];
  let matched = 0;

  for (const rule of rules) {
    const value = values[rule.name];
    if (value == null) {
      if (!truncated) missing.push({ name: rule.name, expected: describe(rule.expect), why: rule.why });
      continue;
    }
    if (passes(value, rule.expect)) matched += 1;
    else
      deviations.push({
        name: rule.name,
        value,
        expected: describe(rule.expect),
        severity: rule.severity,
        why: rule.why,
      });
  }

  deviations.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === "critical" ? -1 : 1));
  return { profile: key, checked: rules.length, matched, deviations, missing, truncated };
};

/** Markdown section for the RCA draft. Returns "" when there is nothing to say. */
export const paramSection = (audit) => {
  if (!audit) return "";
  const lines = ["## Parameter audit", ""];
  lines.push(
    `Checked ${audit.checked} safety-critical parameters against the ${audit.profile || "generic"} golden set — ${audit.matched} match.`,
    ""
  );
  if (audit.truncated) {
    lines.push(
      "> The PARM series was downsampled, so absent parameters could not be distinguished from dropped samples. Missing-parameter checks were skipped.",
      ""
    );
  }
  if (audit.deviations.length) {
    lines.push("| Parameter | Value | Expected | Severity | Why it matters |", "| --- | --- | --- | --- | --- |");
    audit.deviations.forEach((d) =>
      lines.push(`| ${d.name} | ${d.value} | ${d.expected} | ${d.severity} | ${d.why} |`)
    );
    lines.push("");
  } else {
    lines.push("No deviations.", "");
  }
  if (audit.missing.length) {
    lines.push(
      `Not present in the log: ${audit.missing.map((m) => m.name).join(", ")}. Parameter names differ between firmware versions and vehicle types — confirm against this build before treating any of these as unconfigured.`,
      ""
    );
  }
  return lines.join("\n");
};
